import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
  isGold: boolean;
  pulsePhase: number;
  pulseSpeed: number;
}

function createParticle(width: number, height: number): Particle {
  return {
    x: Math.random() * width,
    y: Math.random() * height,
    vx: (Math.random() - 0.5) * 0.3,
    vy: (Math.random() - 0.5) * 0.3,
    radius: Math.random() * 1.5 + 0.5,
    opacity: Math.random() * 0.5 + 0.1,
    isGold: Math.random() > 0.85,
    pulsePhase: Math.random() * Math.PI * 2,
    pulseSpeed: Math.random() * 0.02 + 0.005,
  };
}

export function ParticleConstellation() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let raf = 0;
    const mouse = { x: null as number | null, y: null as number | null };
    let particles: Particle[] = [];
    let sweepAngle = 0;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const finePointer = window.matchMedia('(pointer: fine)').matches;
    const onMouseMove = (e: MouseEvent) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };
    if (finePointer && !reducedMotion) window.addEventListener('mousemove', onMouseMove);

    const count = Math.min(80, Math.floor((canvas.width * canvas.height) / 15000));
    particles = Array.from({ length: count }, () => createParticle(canvas.width, canvas.height));

    const updateParticle = (p: Particle) => {
      p.x += p.vx;
      p.y += p.vy;
      p.pulsePhase += p.pulseSpeed;
      if (p.x < 0) p.x = canvas.width;
      if (p.x > canvas.width) p.x = 0;
      if (p.y < 0) p.y = canvas.height;
      if (p.y > canvas.height) p.y = 0;
      if (mouse.x !== null && mouse.y !== null) {
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 120 && dist > 0) {
          const force = ((120 - dist) / 120) * 0.02;
          p.vx += (dx / dist) * force;
          p.vy += (dy / dist) * force;
        }
      }
      p.vx *= 0.999;
      p.vy *= 0.999;
    };

    const drawParticle = (p: Particle) => {
      const pulse = Math.sin(p.pulsePhase) * 0.3 + 0.7;
      const radius = p.radius * pulse;
      const opacity = p.opacity * pulse;
      ctx.beginPath();
      ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
      if (p.isGold) {
        ctx.fillStyle = `rgba(212, 175, 55, ${opacity * 1.5})`;
        ctx.shadowBlur = 8;
        ctx.shadowColor = 'rgba(212, 175, 55, 0.3)';
      } else {
        ctx.fillStyle = `rgba(148, 163, 184, ${opacity * 0.6})`;
        ctx.shadowBlur = 0;
      }
      ctx.fill();
      ctx.shadowBlur = 0;
    };

    const drawConnections = () => {
      for (let i = 0; i < particles.length; i++) {
        const a = particles[i];
        if (!a) continue;
        for (let j = i + 1; j < particles.length; j++) {
          const b = particles[j];
          if (!b) continue;
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            const alpha = (1 - dist / 150) * 0.12;
            const hasGold = a.isGold || b.isGold;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = hasGold
              ? `rgba(212, 175, 55, ${alpha * 1.2})`
              : `rgba(148, 163, 184, ${alpha * 0.5})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
    };

    const drawRadarSweep = () => {
      const cx = canvas.width * 0.5;
      const cy = canvas.height * 0.4;
      const radius = 250;
      sweepAngle += 0.008;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, radius, sweepAngle, sweepAngle + 0.5);
      ctx.closePath();
      const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
      gradient.addColorStop(0, 'rgba(212, 175, 55, 0.03)');
      gradient.addColorStop(1, 'rgba(212, 175, 55, 0)');
      ctx.fillStyle = gradient;
      ctx.fill();
    };

    const drawFrame = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawRadarSweep();
      particles.forEach((p) => {
        updateParticle(p);
        drawParticle(p);
      });
      drawConnections();
      if (mouse.x !== null && mouse.y !== null) {
        const glow = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 100);
        glow.addColorStop(0, 'rgba(212, 175, 55, 0.03)');
        glow.addColorStop(1, 'rgba(212, 175, 55, 0)');
        ctx.fillStyle = glow;
        ctx.fillRect(mouse.x - 100, mouse.y - 100, 200, 200);
      }
    };

    const animate = () => {
      drawFrame();
      raf = requestAnimationFrame(animate);
    };

    // Render estático: um frame, sem loop nem sweep
    const drawStaticFrame = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => drawParticle(p));
      drawConnections();
    };

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      // O resize zera o bitmap; sem loop ativo, redesenha o frame estático
      if (reducedMotion) drawStaticFrame();
    };
    window.addEventListener('resize', resize);

    const onVisibilityChange = () => {
      cancelAnimationFrame(raf);
      if (!document.hidden && !reducedMotion) animate();
    };

    if (reducedMotion) {
      drawStaticFrame();
    } else {
      animate();
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      if (finePointer && !reducedMotion) window.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-0"
      aria-hidden="true"
    />
  );
}
